import { useRef, useState } from 'react';
import {
  Button, Spinner, Text, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  previewDroneFlightImport, commitDroneFlightImport, type DroneImportResponse,
} from '../../api/drone-flights.api';
import { downloadDroneFlightsTemplate } from '../../utils/template-generator';
import { useT } from '../../i18n';

const useStyles = makeStyles({
  body:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, paddingTop: tokens.spacingVerticalS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  scroll:  { maxHeight: '40vh', overflow: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td:      { padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
});

interface Props { open: boolean; onClose: () => void }

/** Importa vuelos de dron desde Excel: valida (preview), muestra errores por fila y confirma solo sin errores. */
export function DroneFlightImportDialog({ open, onClose }: Props) {
  const s = useStyles();
  const t = useT();
  const di = t.droneImport;
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<DroneImportResponse | null>(null);
  const [done, setDone] = useState<DroneImportResponse | null>(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null); setPreview(null); setDone(null); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { reset(); onClose(); };

  const previewMut = useMutation({
    mutationFn: (f: File) => previewDroneFlightImport(f),
    onSuccess: setPreview,
    onError: () => { setPreview(null); setError(di.readError); },
  });
  const commitMut = useMutation({
    mutationFn: (f: File) => commitDroneFlightImport(f),
    onSuccess: (r) => { setDone(r); qc.invalidateQueries({ queryKey: ['drone-flights'] }); },
    onError: () => setError(di.importError),
  });

  const onPick = (f: File | undefined) => {
    setPreview(null); setDone(null); setError('');
    setFile(f ?? null);
    if (f) previewMut.mutate(f);
  };

  const typeLabel = (ty: string) => (di as Record<string, string>)[`type${ty}`] ?? ty;
  const errors = preview?.errors ?? [];
  const canConfirm = !!file && !!preview && errors.length === 0 && (preview.preview?.length ?? 0) > 0
    && !commitMut.isPending && !done;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) close(); }}>
      <DialogSurface style={{ maxWidth: 760 }}>
        <DialogBody>
          <DialogTitle>{di.title}</DialogTitle>
          <DialogContent>
            <div className={s.body}>
              <Text size={200}>{di.intro}</Text>
              <div className={s.actions}>
                <Button size="small" onClick={() => { void downloadDroneFlightsTemplate(); }}>{di.template}</Button>
                <Button size="small" appearance="primary" onClick={() => fileRef.current?.click()} disabled={previewMut.isPending || commitMut.isPending}>
                  {di.chooseFile}
                </Button>
                <input
                  ref={fileRef} type="file" hidden
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => onPick(e.target.files?.[0])}
                />
                {file && <Text size={200}>{file.name}</Text>}
                {previewMut.isPending && <Spinner size="tiny" label={di.validating} />}
              </div>

              {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}

              {preview && errors.length > 0 && (
                <>
                  <MessageBar intent="error"><MessageBarBody>{di.errorsTitle} ({errors.length})</MessageBarBody></MessageBar>
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead><tr>
                        <th className={s.th}>{di.colRow}</th><th className={s.th}>{di.colField}</th><th className={s.th}>{di.colMessage}</th>
                      </tr></thead>
                      <tbody>
                        {errors.map((e, i) => (
                          <tr key={i}><td className={s.td}>{e._row || '—'}</td><td className={s.td}>{e.field}</td><td className={s.td}>{e.message}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {preview && errors.length === 0 && !done && (
                <>
                  <Text size={200} weight="semibold">{preview.summary.toCreate} {di.summary}</Text>
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead><tr>
                        <th className={s.th}>{di.colRow}</th><th className={s.th}>{di.colDate}</th><th className={s.th}>{di.colType}</th>
                        <th className={s.th}>{di.colLote}</th><th className={s.th}>{di.colValves}</th>
                        <th className={s.th}>{di.colHa}</th><th className={s.th}>{di.colLiters}</th>
                      </tr></thead>
                      <tbody>
                        {(preview.preview ?? []).map(r => (
                          <tr key={r._row}>
                            <td className={s.td}>{r._row}</td>
                            <td className={s.td}>{r.flightDate.slice(0, 10)}</td>
                            <td className={s.td}>{typeLabel(r.flightType)}</td>
                            <td className={s.td}>{r.loteName}</td>
                            <td className={s.td}>{r.valveCodes?.join(', ') ?? ''}</td>
                            <td className={s.td}>{r.areaCoveredHa ?? ''}</td>
                            <td className={s.td}>{r.litersApplied ?? ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {done && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    {di.done}: {done.summary.created}
                    {done.summary.skipped ? ` · ${done.summary.skipped} ${di.skipped}` : ''}
                  </MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>{di.close}</Button>
            <Button
              appearance="primary" disabled={!canConfirm}
              icon={commitMut.isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() => file && commitMut.mutate(file)}
            >
              {commitMut.isPending ? di.importing : di.confirm}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
